import { isUnassignedResult } from "../../components/Pages/Diary/SpaceScanner/SpaceScannerConstants";
import {
  ScanDecision,
  ScanSession,
  ProblematicPattern,
  ConfigSuggestion
} from "./types/ScanHistory.types";

const MIN_FREQUENCY = 3;
const UNUSED_AREA_MIN_SESSIONS = 5;
const CONGESTION_REJECTION_THRESHOLD = 0.5;

const getUniqueSessionCount = (decisions: ScanDecision[]): number =>
  new Set(decisions.map((d) => d.scanSessionId)).size;

const getEarliestTimestamp = (decisions: ScanDecision[]): number =>
  Math.min(...decisions.map((d) => d.timestamp));

const detectRecurringUnassigned = (
  decisions: ScanDecision[]
): ProblematicPattern[] => {
  const unassigned = decisions.filter((d) => isUnassignedResult(d.resultType));
  const patterns: ProblematicPattern[] = [];
  const groups = new Map<string, ScanDecision[]>();

  for (const d of unassigned) {
    const key = `${d.bookingType}::${d.timeSlot}`;
    const group = groups.get(key) ?? [];
    group.push(d);
    groups.set(key, group);
  }

  for (const [key, group] of groups) {
    const sessionCount = getUniqueSessionCount(group);
    if (sessionCount < MIN_FREQUENCY) continue;

    const [bookingType, timeSlot] = key.split("::");
    const avgPartySize = Math.round(
      group.reduce((sum, d) => sum + d.partySize, 0) / group.length
    );

    patterns.push({
      patternType: "recurring_unassigned",
      description: `${avgPartySize}-person ${bookingType} bookings at ${timeSlot} are often unassigned. Consider configuring joined tables.`,
      suggestion:
        "Review table configurations and consider enabling joined tables for larger parties.",
      frequency: sessionCount,
      firstSeen: getEarliestTimestamp(group),
      bookingType,
      timeSlot
    });
  }

  return patterns;
};

const detectTimeSlotCongestion = (
  decisions: ScanDecision[]
): ProblematicPattern[] => {
  const patterns: ProblematicPattern[] = [];
  const groups = new Map<string, ScanDecision[]>();

  for (const d of decisions) {
    if (!d.timeSlot) continue;
    const group = groups.get(d.timeSlot) ?? [];
    group.push(d);
    groups.set(d.timeSlot, group);
  }

  for (const [timeSlot, group] of groups) {
    const sessionCount = getUniqueSessionCount(group);
    if (sessionCount < MIN_FREQUENCY) continue;

    const rejectedCount = group.filter((d) => !d.accepted).length;
    const rejectionRate = rejectedCount / group.length;
    if (rejectionRate <= CONGESTION_REJECTION_THRESHOLD) continue;

    patterns.push({
      patternType: "time_slot_congestion",
      description: `The ${timeSlot} slot frequently has optimisation issues.`,
      suggestion:
        "Consider extending booking hours or adding capacity during this period.",
      frequency: sessionCount,
      firstSeen: getEarliestTimestamp(group),
      timeSlot
    });
  }

  return patterns;
};

const detectUnusedAreas = (
  sessions: ScanSession[],
  decisions: ScanDecision[]
): ProblematicPattern[] => {
  if (sessions.length < UNUSED_AREA_MIN_SESSIONS) return [];

  const patterns: ProblematicPattern[] = [];
  const fromAreas = new Set<string>();
  const toAreas = new Set<string>();

  for (const d of decisions) {
    if (d.fromAreaName) fromAreas.add(d.fromAreaName);
    if (d.toAreaName) toAreas.add(d.toAreaName);
  }

  for (const areaName of fromAreas) {
    if (toAreas.has(areaName)) continue;

    patterns.push({
      patternType: "unused_area",
      description: `${areaName} is never suggested as a destination. It may need configuration review.`,
      suggestion:
        "Check that this area has the correct booking types and capacity settings configured.",
      frequency: sessions.length,
      firstSeen: Math.min(...sessions.map((s) => s.timestamp)),
      areaName
    });
  }

  return patterns;
};

export const identifyProblematicPatterns = (
  sessions: ScanSession[],
  decisions: ScanDecision[]
): ProblematicPattern[] => {
  if (decisions.length === 0) return [];

  return [
    ...detectRecurringUnassigned(decisions),
    ...detectTimeSlotCongestion(decisions),
    ...detectUnusedAreas(sessions, decisions)
  ];
};

export const suggestConfigImprovements = (
  patterns: ProblematicPattern[],
  decisions: ScanDecision[]
): ConfigSuggestion[] => {
  if (patterns.length === 0) return [];

  const suggestions: ConfigSuggestion[] = [];

  for (const pattern of patterns) {
    switch (pattern.patternType) {
      case "recurring_unassigned": {
        const relevantDecisions = decisions.filter(
          (d) =>
            isUnassignedResult(d.resultType) &&
            d.bookingType === pattern.bookingType &&
            d.timeSlot === pattern.timeSlot
        );

        const hasNoTablesConfigured = relevantDecisions.some(
          (d) => d.resultType === "unassigned_no_tables_configured"
        );
        const hasTableTooSmall = relevantDecisions.some(
          (d) => d.resultType === "unassigned_table_too_small"
        );

        if (hasNoTablesConfigured) {
          suggestions.push({
            description: `Your '${pattern.bookingType}' booking type isn't configured to use certain tables. Adding table assignments could reduce unassigned bookings.`,
            estimatedImpact: `Could resolve recurring unassigned ${pattern.bookingType} bookings at ${pattern.timeSlot}.`,
            settingsArea: "Booking type settings"
          });
        }

        if (hasTableTooSmall) {
          const avgPartySize = Math.round(
            relevantDecisions.reduce((sum, d) => sum + d.partySize, 0) /
              relevantDecisions.length
          );
          suggestions.push({
            description: `Consider configuring joined tables to accommodate ${pattern.bookingType} bookings of ${avgPartySize}+ guests.`,
            estimatedImpact: `Could seat larger ${pattern.bookingType} parties that currently can't be assigned.`,
            settingsArea: "Table settings"
          });
        }
        break;
      }
      case "time_slot_congestion":
        suggestions.push({
          description: `The ${pattern.timeSlot} slot is consistently congested. Consider extending booking hours or adding capacity.`,
          estimatedImpact: `Could improve optimisation outcomes during the ${pattern.timeSlot} period.`,
          settingsArea: "Booking hours"
        });
        break;
      case "unused_area":
        suggestions.push({
          description: `${pattern.areaName} is never used as a destination. Review its booking type and capacity settings.`,
          estimatedImpact: `Could unlock additional seating capacity by making ${pattern.areaName} available for optimisation.`,
          settingsArea: "Area settings"
        });
        break;
    }
  }

  return suggestions;
};
